// @flow
import _ from 'lodash';
import React from 'react';
import jQuery from 'jquery';
import moment from 'moment';

import Ariane from 'PMT/components/Ariane';
import ArrayUtils from 'PMT/utils/ArrayUtils';
import ApiConstants from 'PMT/constants/ApiConstants';
import CurrencyUtils from 'PMT/utils/CurrencyUtils';
import DateUtils from 'PMT/utils/DateUtils';
import I18n from 'PMT/i18n/I18n';
import Order from 'PMT/entities/order/Order';
import OrderModeChooser from 'PMT/components/form/OrderModeChooser';
import OrderViewUtils from 'PMT/utils/OrderViewUtils';
import PaymentMethod from 'PMT/constants/PaymentMethod';
import Timelapse from 'PMT/entities/Timelapse';

import AbstractComponent from 'components/AbstractComponent';
import Events from 'constants/Events';
import { RestaurantsGroupAction, RouteAction } from 'actions';
import { ApiConsumersStore, OrderStatisticsStore, RestaurantStore, SessionStore } from 'stores';
import RoutingEnum from 'constants/RoutingEnum';
import ViewConstants from 'constants/ViewConstants';

import Filters from './components/Filters';
import View from './View';

import './style.scss';

export default class OrderStatistics extends AbstractComponent {
  constructor() {
    super();

    this.arianeIcon = 'fa-line-chart';
    this.arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: '',
      },
      {
        name: I18n.tr('menu.order.statistics'),
        link: '',
      },
    ];

    this.sourcesList = [];
  }

  state = {
    orderStatisticsState: OrderStatisticsStore.getState(),
    apiConsumersState: ApiConsumersStore.getState(),
    sourcesList: [],
    modes: [],
    paymentMethods: [],
    timelapse: SessionStore.getTimelapse(),
    salesFilters: ['hour'],
    weekdaysFilters: _.keys(this.weekdaysFilters),
    restaurantsIds: [SessionStore.getRestaurantId()],
    restaurant: RestaurantStore.getRestaurant()
  };

  componentDidMount() {
    if (!RestaurantStore.getRestaurant().haveFeatureOrder()) {
      RouteAction.redirectToFeatureNonActivated();
      return;
    }

    ApiConsumersStore.addListener(this.onFetchApiConsumers);
    OrderStatisticsStore.addListener(this.onFetchStatistics);

    this.loadApiConsumers();
    this.loadStatistics();
  }

  componentWillUnmount() {
    ApiConsumersStore.removeListener(this.onFetchApiConsumers);
    OrderStatisticsStore.removeListener(this.onFetchStatistics);
  }

  get orderModes() {
    return _.keys(Order.OrderMode).map(mode => {
      return {
        label: OrderViewUtils.getLabelForMode(Order.OrderMode[mode]),
        value: mode,
      };
    });
  }

  get paymentMethods() {
    return _.keys(PaymentMethod).map(value => {
      return {
        label: I18n.tr(`payments.method.${value}`),
        value,
      };
    });
  }

  get salesFilters() {
    return {
      hour: {
        label: I18n.tr('order.statistics.salesFilters.hour'),
        filter: 'YYYY-MM-DD HH:mm',
      },
      day: {
        label: I18n.tr('order.statistics.salesFilters.day'),
        filter: 'YYYY-MM-DD',
      },
      week: {
        label: I18n.tr('order.statistics.salesFilters.week'),
        filter: 'YYYY-MM-DD',
      },
      month: {
        label: I18n.tr('order.statistics.salesFilters.month'),
        filter: 'YYYY-MM',
      },
    };
  }

  get weekdaysFilters() {
    return {
      monday: {
        label: I18n.tr('global.days.monday'),
        filter: 1,
        color: '#0074D9',
      },
      tuesday: {
        label: I18n.tr('global.days.tuesday'),
        filter: 2,
        color: '#7FDBFF',
      },
      wednesday: {
        label: I18n.tr('global.days.wednesday'),
        filter: 3,
        color: '#2ECC40',
      },
      thursday: {
        label: I18n.tr('global.days.thursday'),
        filter: 4,
        color: '#94FFA6',
      },
      friday: {
        label: I18n.tr('global.days.friday'),
        filter: 5,
        color: '#FFDC00',
      },
      saturday: {
        label: I18n.tr('global.days.saturday'),
        filter: 6,
        color: '#FF851B',
      },
      sunday: {
        label: I18n.tr('global.days.sunday'),
        filter: 7,
        color: '#FF4136',
      },
      average: {
        label: I18n.tr('order.statistics.filters.average'),
        filter: -1,
        color: '#000000',
      },
    };
  }

  loadApiConsumers() {
    RestaurantsGroupAction.loadApiConsumers(SessionStore.getRestaurantsGroupId(), {
      withOther: true,
      withParents: false,
    });
  }

  /**
   * API method allows us to filter directly through the call
   * however, for better ux we let the front filter the data
   */
  loadStatistics() {
    RestaurantsGroupAction.loadOrderStatistics(
      SessionStore.getRestaurantsGroupId(),
      SessionStore.getTimelapse(),
      [], //this.state.modes,
      [], //this.state.sourcesList,
      [], //this.state.paymentMethods,
      this.state.restaurantsIds
    );
  }

  onUpdateTimelapse = timelapse => {
    SessionStore.saveTimelapse(timelapse);

    this.setState(
      {
        timelapse,
      },
      this.loadStatistics
    );
  };

  onUpdateModes = modes => {
    this.setState(
      {
        modes,
      },
      this.onFetchStatistics
    );
  };

  onUpdateFilters = (options, key) => {
    const newState = {};

    newState[key] = options.map(option => {
      return key == 'modes' ? option : option.value;
    });

    this.setState(newState, this.onFetchStatistics);
  };

  onFetchApiConsumers = () => {
    const apiConsumersState = ApiConsumersStore.getState();

    if (!_.isNull(apiConsumersState.data)) {
      this.sourcesList = apiConsumersState.data.apiConsumers.map(({ id, name }) => {
        return {
          label: name,
          value: id,
        };
      });
      this.sourcesList.push({
        label: I18n.tr('order.statistics.other'),
        value: 0,
      });
    }

    this.setState({
      apiConsumersState,
    });
  };

  onFetchStatistics = () => {
    try {
      const orderStatisticsState = OrderStatisticsStore.getState();

      if (!_.isNull(orderStatisticsState.data)) {
        const orderStatistics = orderStatisticsState.data;

        if (!_.isEmpty(orderStatistics.items)) {
          const defaultValues = {
            nbOrders: 0,
            averageTTC: 0,
            turnoverTTC: 0,
          };

          orderStatistics.sums = {
            modes: {},
            sources: {},
            payments: {},
            total: _.clone(defaultValues),
          };

          _.forEach(orderStatistics.items, item => {
            // item.hour is a string formatted, on a UTC date.
            // We need to update it to be a current timezone date
            const format = 'YYYY-MM-DD HH:mm';
            item.hour = moment.utc(item.hour, format).local().format(format);

            if (
              (_.isEmpty(this.state.modes) ||
                this.state.modes.includes(Order.OrderMode[item.mode])) &&
              (_.isEmpty(this.state.paymentMethods) ||
                this.state.paymentMethods.includes(item.paymentMethod)) &&
              (_.isEmpty(this.state.sourcesList) || this.state.sourcesList.includes(item.source))
            ) {
              if (_.isEmpty(orderStatistics.sums.modes[item.mode])) {
                orderStatistics.sums.modes[item.mode] = _.clone(defaultValues);
              }
              if (_.isEmpty(orderStatistics.sums.sources[item.source])) {
                orderStatistics.sums.sources[item.source] = _.clone(defaultValues);
              }
              if (_.isEmpty(orderStatistics.sums.payments[item.paymentMethod])) {
                orderStatistics.sums.payments[item.paymentMethod] = _.clone(defaultValues);
              }

              orderStatistics.sums.modes[item.mode].nbOrders += item.nbOrders;
              orderStatistics.sums.modes[item.mode].turnoverTTC += item.turnoverTTC;
              orderStatistics.sums.modes[item.mode].averageTTC =
                orderStatistics.sums.modes[item.mode].turnoverTTC /
                orderStatistics.sums.modes[item.mode].nbOrders;

              orderStatistics.sums.sources[item.source].nbOrders += item.nbOrders;
              orderStatistics.sums.sources[item.source].turnoverTTC += item.turnoverTTC;
              orderStatistics.sums.sources[item.source].averageTTC =
                orderStatistics.sums.sources[item.source].turnoverTTC /
                orderStatistics.sums.sources[item.source].nbOrders;

              orderStatistics.sums.payments[item.paymentMethod].nbOrders += item.nbOrders;
              orderStatistics.sums.payments[item.paymentMethod].turnoverTTC += item.turnoverTTC;
              orderStatistics.sums.payments[item.paymentMethod].averageTTC =
                orderStatistics.sums.payments[item.paymentMethod].turnoverTTC /
                orderStatistics.sums.payments[item.paymentMethod].nbOrders;

              orderStatistics.sums.total.nbOrders += item.nbOrders;
              orderStatistics.sums.total.turnoverTTC += item.turnoverTTC;
              orderStatistics.sums.total.averageTTC =
                orderStatistics.sums.total.turnoverTTC / orderStatistics.sums.total.nbOrders;
            }
          });
        }
      }

      this.setState(
        {
          orderStatisticsState,
        },
        !_.isEmpty(orderStatisticsState.data) && !_.isEmpty(orderStatisticsState.data.items)
          ? this.onUpdateGraph
          : null
      );
    } catch (e) {
      console.error(e);
    }
  };

  handleChangeFraphFilters = (key, filters) => {
    const newState = this.state;
    newState[key] = filters;

    this.setState(newState, this.onUpdateGraph);
  };

  onUpdateGraph = () => {
    let format = !_.isNull(this.salesFilters[this.state.salesFilters[0]])
      ? this.salesFilters[this.state.salesFilters[0]].filter
      : 'YYYY-MM-DD HH:mm';

    let graphDataByFilterView = {};
    _.forEach(
      this.state.orderStatisticsState.data.items,
      ({ mode, paymentMethod, source, hour, turnoverTTC, nbOrders }) => {
        if (
          (_.isEmpty(this.state.modes) || this.state.modes.includes(Order.OrderMode[mode])) &&
          (_.isEmpty(this.state.paymentMethods) ||
            this.state.paymentMethods.includes(paymentMethod)) &&
          (_.isEmpty(this.state.sourcesList) || this.state.sourcesList.includes(source))
        ) {
          let period = moment(hour).format(format);
          if (this.state.salesFilters.includes('week')) {
            period = moment(moment(hour).format('YYYY'))
              .add(moment(hour).isoWeek() - 1, 'weeks')
              .add(1, 'day')
              .format('YYYY-MM-DD');
          }

          if (_.isEmpty(graphDataByFilterView[period])) {
            graphDataByFilterView[period] = {
              averageTTC: 0,
              turnoverTTC: 0,
              nbOrders: 0,
            };
          }

          graphDataByFilterView[period].turnoverTTC += turnoverTTC;
          graphDataByFilterView[period].nbOrders += nbOrders;
          graphDataByFilterView[period].averageTTC =
            graphDataByFilterView[period].turnoverTTC / graphDataByFilterView[period].nbOrders;
        }
      }
    );

    const graphData = _.keys(graphDataByFilterView).map(moment => {
      return {
        moment,
        turnoverTTC: graphDataByFilterView[moment].turnoverTTC,
        averageTTC: graphDataByFilterView[moment].averageTTC,
      };
    });

    const filledGraphData = [];
    let m = moment(this.state.timelapse.getFromTimeAsDate());

    for (let i = 0; i < graphData.length; i++) {
      while (!m.isSame(moment(graphData[i].moment), this.state.salesFilters[0])) {
        filledGraphData.push({
          moment: m.format(format),
          turnoverTTC: 0,
          averageTTC: 0,
        });
        m = m.add(1, this.state.salesFilters[0]);
      }
      filledGraphData.push(graphData[i]);
      m = m.add(1, this.state.salesFilters[0]);
    }

    while (m.diff(moment(this.state.timelapse.getToTimeAsDate())) < 0) {
      filledGraphData.push({
        moment: m.format(format),
        turnoverTTC: 0,
        averageTTC: 0,
      });
      m = m.add(1, this.state.salesFilters[0]);
    }

    let dataByWeekday = {};
    for (let j = 0; j <= 24; j++) {
      let m = moment(j * 3600000);
      dataByWeekday[m.format('1970-01-01 HH:mm')] = [0, 0, 0, 0, 0, 0, 0, 0];
      m = m.add(1, 'hour');
    }

    _.forEach(
      this.state.orderStatisticsState.data.items,
      ({ mode, paymentMethod, source, hour, turnoverTTC }) => {
        if (
          (_.isEmpty(this.state.modes) || this.state.modes.includes(Order.OrderMode[mode])) &&
          (_.isEmpty(this.state.paymentMethods) ||
            this.state.paymentMethods.includes(paymentMethod)) &&
          (_.isEmpty(this.state.sourcesList) || this.state.sourcesList.includes(source))
        ) {
          const period = moment(hour).format('1970-01-01 HH:mm');
          const weekday = moment(hour).isoWeekday();

          if (_.isEmpty(dataByWeekday[period])) {
            dataByWeekday[period] = [0, 0, 0, 0, 0, 0, 0, 0];
          }

          dataByWeekday[period][weekday] += turnoverTTC;
        }
      }
    );

    let nbDays = [];
    for (let i = 1; i <= 7; i++) {
      nbDays.push(
        DateUtils.nbWeekdaysBetween(
          this.state.timelapse.getFromTimeAsDate(),
          this.state.timelapse.getToTimeAsDate(),
          i
        )
      );
    }

    const nbTotalDays = nbDays.reduce((a, b) => a + b);

    const graphDataByWeekday = _.keys(dataByWeekday).map(moment => {
      let data = { moment };

      this.state.weekdaysFilters.map(filter => {
        switch (filter) {
          case 'average':
            data['average'] =
              (dataByWeekday[moment][1] +
                dataByWeekday[moment][2] +
                dataByWeekday[moment][3] +
                dataByWeekday[moment][4] +
                dataByWeekday[moment][5] +
                dataByWeekday[moment][6] +
                dataByWeekday[moment][7]) /
              nbTotalDays;
            break;
          default:
            data[filter] =
              nbDays[this.weekdaysFilters[filter].filter - 1] > 0
                ? dataByWeekday[moment][this.weekdaysFilters[filter].filter] /
                  nbDays[this.weekdaysFilters[filter].filter - 1]
                : 0;
        }
      });

      return data;
    });

    $('#area, #donut-sources, #donut-modes, #donut-payments, #area-by-weekday').html('');

    Morris.Area({
      element: 'area',
      data: filledGraphData,
      xkey: 'moment',
      ykeys: ['turnoverTTC', 'averageTTC'],
      labels: [I18n.tr('order.statistics.turnover'), I18n.tr('order.statistics.average')],
      hideHover: 'auto',
      resize: true,
      pointSize: 0,
      lineWidth: 2,
      smooth: false,
      fillOpacity: 0.1,
      lineColors: ['#5CB85C', '#337AB7'],
      behaveLikeLine: true,
      dateFormat: date => {
        return moment(date).format(format);
      },
      yLabelFormat: value => {
        return CurrencyUtils.formatMillionPriceWithCurrency(value,
            this.state.restaurant.currency);
      },
    });

    Morris.Donut({
      element: 'donut-modes',
      colors: ViewConstants.Graph.DONUT_ORDERS_COLORS,
      data: _.keys(this.state.orderStatisticsState.data.sums.modes).map(mode => {
        return {
          label: OrderViewUtils.getLabelForMode(Order.OrderMode[mode]),
          value: (
            (this.state.orderStatisticsState.data.sums.modes[mode].turnoverTTC * 100) /
            this.state.orderStatisticsState.data.sums.total.turnoverTTC
          ).toFixed(2),
        };
      }),
      formatter: value => {
        return `${value} %`;
      },
      resize: true,
    });

    Morris.Donut({
      element: 'donut-sources',
      colors: ViewConstants.Graph.DONUT_ORDERS_COLORS,
      data: _.keys(this.state.orderStatisticsState.data.sums.sources).map(source => {
        const sourceObj = _.find(this.sourcesList, s => {
          // s.value is integer, source is string, but there are both the int id.
          return s.value === _.toInteger(source);
        });

        return {
          label:
            source == 0
              ? I18n.tr('order.statistics.other')
              : _.isNil(sourceObj)
              ? 'N/A'
              : sourceObj.label,
          value: (
            (this.state.orderStatisticsState.data.sums.sources[source].turnoverTTC * 100) /
            this.state.orderStatisticsState.data.sums.total.turnoverTTC
          ).toFixed(2),
        };
      }),
      formatter: value => {
        return `${value} %`;
      },
      resize: true,
    });

    Morris.Donut({
      element: 'donut-payments',
      colors: ViewConstants.Graph.DONUT_ORDERS_COLORS,
      data: _.keys(this.state.orderStatisticsState.data.sums.payments).map(payment => {
        return {
          label: I18n.tr(`payments.method.${payment}`),
          value: (
            (this.state.orderStatisticsState.data.sums.payments[payment].turnoverTTC * 100) /
            this.state.orderStatisticsState.data.sums.total.turnoverTTC
          ).toFixed(2),
        };
      }),
      formatter: value => {
        return `${value} %`;
      },
      resize: true,
    });

    Morris.Area({
      element: 'area-by-weekday',
      data: graphDataByWeekday,
      xkey: 'moment',
      ykeys: this.state.weekdaysFilters,
      labels: this.state.weekdaysFilters.map(filter => this.weekdaysFilters[filter].label),
      hideHover: 'auto',
      resize: true,
      pointSize: 0,
      lineWidth: 2,
      smooth: false,
      fillOpacity: 0.1,
      lineColors: this.state.weekdaysFilters.map(filter => this.weekdaysFilters[filter].color),
      behaveLikeLine: true,
      dateFormat: date => {
        return moment(date).format('HH:mm');
      },
      xLabelFormat: date => {
        return moment(date).format('HH:mm');
      },
      yLabelFormat: value => {
        return CurrencyUtils.formatMillionPriceWithCurrency(value,
            this.state.restaurant.currency);
      },
    });
  };

  render(): Object {
    const orderStatisticsState = this.state.orderStatisticsState;
    const apiConsumersState = this.state.apiConsumersState;

    if (
      _.isEqual(OrderStatisticsStore.getInitialState(), orderStatisticsState) ||
      _.isEqual(ApiConsumersStore.getInitialState(), apiConsumersState)
    ) {
      return this.renderLoading();
    }

    return (
      <div className="stat_page">
        <Ariane icon={this.arianeIcon} details={this.arianeDetails} />

        {!apiConsumersState.isFetching && (
          <Filters
            isLoading={this.state.orderStatisticsState.isFetching}
            sourcesList={this.state.sourcesList}
            modes={this.state.modes}
            paymentMethods={this.state.paymentMethods}
            timelapse={this.state.timelapse}
            updateFilters={this.onUpdateFilters}
            updateModes={this.onUpdateModes}
            updateTimelapse={this.onUpdateTimelapse}
          />
        )}

        {orderStatisticsState.isFetching ? (
          this.renderOnLoadingContent()
        ) : (
          <View
            data={orderStatisticsState.data}
            orderModes={this.orderModes}
            paymentMethods={this.paymentMethods}
            sourcesList={this.sourcesList}
            apiError={orderStatisticsState.apiError}
            salesFilters={this.salesFilters}
            salesFiltersState={this.state.salesFilters}
            weekdaysFilters={this.weekdaysFilters}
            weekdaysFiltersState={this.state.weekdaysFilters}
            changeFilters={this.handleChangeFraphFilters}
            currencyCode={this.state.restaurant.currency}
          />
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/pages/order/statistics/index.js